import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// Per-table hover state for the row/column drag handles. Lives inside the
// <TableProvider> boundary so each table has its own hover state — moving
// the cursor between two tables in the same doc doesn't bleed handle
// visibility between them.
//
// "Sticky" hide: cells call hideDeferred() on mouseleave, which schedules a
// short timeout. If the pointer moves onto the floating handle, the handle's
// own onMouseEnter calls cancelHide() to keep itself visible. This is the
// Notion pattern — without it the handle vanishes the moment the pointer
// crosses from cell border to handle.

export type TableHoverState = { row: number | null; col: number | null };

interface ContextValue {
    hovered: TableHoverState;
    show: (row: number, col: number) => void;
    hideDeferred: () => void;
    cancelHide: () => void;
    clearImmediately: () => void;
}

const TableHoverContext = createContext<ContextValue | null>(null);

const HIDE_DELAY_MS = 120;

export function TableHoverProvider({ children }: { children: React.ReactNode }) {
    const [hovered, setHovered] = useState<TableHoverState>({ row: null, col: null });
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelHide = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const show = useCallback(
        (row: number, col: number) => {
            cancelHide();
            setHovered((prev) => (prev.row === row && prev.col === col ? prev : { row, col }));
        },
        [cancelHide],
    );

    const hideDeferred = useCallback(() => {
        cancelHide();
        timerRef.current = setTimeout(() => {
            setHovered({ row: null, col: null });
            timerRef.current = null;
        }, HIDE_DELAY_MS);
    }, [cancelHide]);

    const clearImmediately = useCallback(() => {
        cancelHide();
        setHovered({ row: null, col: null });
    }, [cancelHide]);

    const value = useMemo(
        () => ({ hovered, show, hideDeferred, cancelHide, clearImmediately }),
        [hovered, show, hideDeferred, cancelHide, clearImmediately],
    );

    return <TableHoverContext.Provider value={value}>{children}</TableHoverContext.Provider>;
}

export function useTableHover(): ContextValue {
    const ctx = useContext(TableHoverContext);
    if (!ctx) {
        // Defensive: cells may render in non-table contexts (paste preview,
        // history view); return a no-op shape so they don't crash.
        return {
            hovered: { row: null, col: null },
            show: () => {},
            hideDeferred: () => {},
            cancelHide: () => {},
            clearImmediately: () => {},
        };
    }
    return ctx;
}
